import * as _ from 'lodash';
import {
  SlickPlugin,
  SlickGrid,
  SlickEventData,
  SlickEventHandler,
  OnCellChangeEventArgs,
  Column,
  SlickDataView,
  OnRowsOrCountChangedEventArgs,
} from 'slickgrid';
import { applyFormatterResult } from '../helpers';

interface FooterTotal {
  value: number;
  fromSelection: boolean;
}

export class RowTotalsPlugin implements SlickPlugin {
  pluginName = 'RowTotals' as const;

  private readonly _eventHandler = new SlickEventHandler();
  private _grid: SlickGrid;

  public init(grid: SlickGrid): void {
    this._grid = grid;

    this.updateAllTotals();

    this._eventHandler.subscribe(
      grid.onCellChange,
      this.onCellChanged.bind(this),
    );
    this._eventHandler.subscribe(
      grid.onColumnsReordered,
      this.updateAllTotals.bind(this),
    );
    this._eventHandler.subscribe(
      grid.onColumnsResized,
      this.updateAllTotals.bind(this),
    );
    this._eventHandler.subscribe(
      grid.onSelectedRowsChanged,
      this.updateAllTotals.bind(this),
    );

    const dv: SlickDataView = this._grid.getData();
    this._eventHandler.subscribe(
      dv.onRowsOrCountChanged,
      this.onRowsOrCountChanged.bind(this),
    );
  }

  public destroy(): void {
    this._eventHandler.unsubscribeAll();
  }

  private onCellChanged(
    e: SlickEventData<OnCellChangeEventArgs>,
    args: OnCellChangeEventArgs,
  ) {
    this.updateTotal(args.column);
  }

  private onRowsOrCountChanged(
    e: SlickEventData<OnRowsOrCountChangedEventArgs>,
    args: OnRowsOrCountChangedEventArgs,
  ) {
    this.updateAllTotals();
  }

  updateAllTotals() {
    if (this._grid.getFooterRowColumn(0)) {
      this._grid.getColumns().forEach((col) => this.updateTotal(col));
    }
  }

  updateTotal(column: Column) {
    let total: FooterTotal | null = null;
    if (column.type === 'number' && !column.noFooterTotals) {
      total = this.calculateTotal(column);
    }
    this.displayTotal(column, total);
  }

  calculateTotal(column: Column): FooterTotal {
    const dv = this._grid.getData() as SlickDataView;
    const field = _.toString(column.field);
    const selectedRows = this._grid.getSelectedRows();
    const fromSelection = selectedRows.length > 0;
    const rowIndexes = fromSelection ? selectedRows : _.range(dv.getLength());

    const value = rowIndexes.reduce((sum, i) => {
      const item = dv.getItem(i);
      return sum + (Number(item?.[field]) || 0);
    }, 0);

    return { value, fromSelection };
  }

  displayTotal(column: Column, total: FooterTotal | null) {
    const grid = this._grid;

    const content = total
      ? (column.formatter
          ? column.formatter(-1, -1, total.value, column, {}, grid)
          : total.value.toString()) || ''
      : '';

    const columnElement = grid.getFooterRowColumn(column.id);
    if (!columnElement) {
      console.warn(
        `Cannot display footer total '${content}'. Could not get footer row column. Column id: ${column.id}`,
      );
      return;
    }

    columnElement.classList.toggle('selected', !!total?.fromSelection);

    applyFormatterResult(columnElement, content);
  }
}
